import { CloseOutlined, SendOutlined } from "@ant-design/icons";
import { Button, Empty, Form, Input, Space } from "antd";
import TemplateSelector from "components/apps/conversations/createChat/TemplateSelector";
import { phoneNumbersSelector } from "appredux/selectors";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { colorsIcon, messageTypes } from "utils/static";
import { isEmpty } from "lodash-es";
import {
  resolveScheduleContactId,
  resolveTemplateVarValue,
} from "./scheduleDetailResolvers";

const TEMPLATE_VAR_MAP = {
  229: [{ key: 1, content: "greeting" }, { key: 2, content: "customer_name" }, { key: 3, content: "date" }, { key: 4, content: "start_time" }, { key: 5, content: "end_time" }, { key: 6, content: "start_time" }],
  245: [{ key: 1, content: "customer_name" }, { key: 2, content: "start_time" }, { key: 3, content: "end_time" }],
  34:  [{ key: 1, content: "customer_name" }, { key: 2, content: "start_time" }],
  142: [{ key: 1, content: "customer_name" }, { key: 2, content: "empty" }, { key: 3, content: "start_time" }, { key: 4, content: "end_time" }],
  62:  [{ key: 1, content: "greeting" }, { key: 2, content: "customer_name" }, { key: 3, content: "empty" }],
  252: [{ key: 1, content: "greeting" }, { key: 2, content: "customer_name" }, { key: 3, content: "empty" }, { key: 4, content: "date" }, { key: 5, content: "start_time" }, { key: 6, content: "end_time" }],
  65:  [{ key: 1, content: "greeting" }, { key: 2, content: "customer_name" }, { key: 3, content: "empty" }],
  18:  [{ key: 1, content: "customer_name" }],
  12:  [{ key: 1, content: "customer_name" }, { key: 2, content: "start_time" }, { key: 3, content: "end_time" }],
  11:  [{ key: 1, content: "customer_name" }, { key: 2, content: "start_time" }, { key: 3, content: "end_time" }],
  13:  [{ key: 1, content: "customer_name" }, { key: 2, content: "start_time" }, { key: 3, content: "end_time" }],
  15:  [{ key: 1, content: "customer_name" }, { key: 2, content: "empty" }],
  75:  [{ key: 1, content: "greeting" }, { key: 2, content: "customer_name" }],
  83:  [{ key: 1, content: "greeting" }, { key: 2, content: "customer_name" }, { key: 3, content: "company_name" }],
  113: [{ key: 1, content: "customer_name" }],
  61:  [{ key: 1, content: "greeting" }, { key: 2, content: "customer_name" }, { key: 3, content: "date" }, { key: 4, content: "start_time" }, { key: 5, content: "end_time" }],
  59:  [{ key: 1, content: "greeting" }, { key: 2, content: "customer_name" }, { key: 3, content: "date" }, { key: 4, content: "start_time" }],
  60:  [{ key: 1, content: "greeting" }, { key: 2, content: "customer_name" }, { key: 3, content: "date" }, { key: 4, content: "start_time" }],
  24:  [{ key: 1, content: "customer_name" }, { key: 2, content: "agent_name" }],
  32:  [{ key: 1, content: "customer_name" }, { key: 2, content: "date" }, { key: 3, content: "start_time" }],
  33:  [{ key: 1, content: "greeting" }, { key: 2, content: "customer_name" }, { key: 3, content: "date" }, { key: 4, content: "start_time" }],
  64:  [{ key: 1, content: "greeting" }, { key: 2, content: "customer_name" }, { key: 3, content: "custom1" }],
};

const DEFAULT_TEMPLATE_VARS = [
  { key: 1, content: "customer_name" },
  { key: 2, content: "start_time" },
  { key: 3, content: "end_time" },
];

/**
 * Meeting reminder: selalu Kontak + Template (tanpa toggle UI).
 * Prefills template body {{1}}..{{3}}: nama kontak + jam slot (konvensi produk).
 */
const MeetingReminderForm = ({
  onSubmit,
  onCancel,
  scheduleDetail,
  timeSource,
  zoomTemplates,
  phoneNumberId,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const phoneNumbers = useSelector(phoneNumbersSelector);

  const channelLabel = useMemo(() => {
    if (phoneNumberId == null) return "";
    const row = phoneNumbers?.find(
      (p) => Number(p.id) === Number(phoneNumberId)
    );
    if (!row) return String(phoneNumberId);
    if (row.name && row.phone_number) {
      return `${row.name} (${row.phone_number})`;
    }
    return row.name || row.phone_number || String(phoneNumberId);
  }, [phoneNumberId, phoneNumbers]);

  const initialValues = useMemo(
    () => ({
      receiver_type: "contact",
      message_type: "template",
      contact_id: resolveScheduleContactId(scheduleDetail),
      message_text: "",
      type_id: messageTypes.TEXT,
      attachment_key: "",
      attachment: [],
    }),
    [scheduleDetail]
  );

  useEffect(() => {
    form.setFieldsValue({
      contact_id: resolveScheduleContactId(scheduleDetail),
    });
  }, [scheduleDetail, form]);

  const selectedTemplateHandler = useCallback(
    (val, selected) => {
      const mapping = TEMPLATE_VAR_MAP[Number(val)] ?? DEFAULT_TEMPLATE_VARS;
      const context = { scheduleDetail, timeSource };
      console.log("[MeetingReminder] template selected", { templateId: val, mapping, scheduleDetail, timeSource });

      if (!isEmpty(selected?.payload?.body)) {
        const templateBody = selected.payload.body.map((row, index) => {
          const varDef = mapping.find((m) => m.key === index + 1);
          const resolved = varDef
            ? resolveTemplateVarValue(varDef.content, context)
            : "";
          console.log(`[MeetingReminder] {{${index + 1}}}`, { varDef, resolved, fallback: row });
          return {
            value: resolved !== "" ? resolved : row,
            id: index + 1,
            color: colorsIcon[index],
          };
        });
        form.setFieldValue("template_body", templateBody);
      }
      if (!isEmpty(selected?.payload?.header)) {
        const key = Object.keys(selected.payload.header)[0];
        form.setFieldsValue({ attachment_key: key, attachment: [] });
      }
    },
    [form, scheduleDetail, timeSource]
  );

  const hasTemplates = zoomTemplates.length > 0;
  const c = scheduleDetail?.contact;

  return (
    <Form
      layout="vertical"
      initialValues={initialValues}
      form={form}
      name="meetingReminderChat"
      onFinish={onSubmit}
    >
      <Form.Item name="receiver_type" hidden>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name="message_type" hidden>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name="contact_id" hidden>
        <Input type="hidden" />
      </Form.Item>

      {channelLabel ? (
        <p className="mb-3 text-sm text-neutral-600">
          {t("meeting_management.reminder_from_channel", {
            channel: channelLabel,
          })}
        </p>
      ) : null}

      <Form.Item label={t("to")}>
        <div className="space-y-1 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-900">
          <div>
            <span className="font-medium text-neutral-500">
              {t("name")}:{" "}
            </span>
            {c?.name ?? "—"}
          </div>
          <div>
            <span className="font-medium text-neutral-500">
              {t("company")}:{" "}
            </span>
            {c?.company ?? "—"}
          </div>
          <div>
            <span className="font-medium text-neutral-500">
              {t("phone")}:{" "}
            </span>
            {c?.phone ?? "—"}
          </div>
        </div>
      </Form.Item>

      {!hasTemplates && (
        <Empty
          className="my-4"
          description={t("meeting_management.reminder_no_zoom_templates")}
        />
      )}

      <TemplateSelector
        rules={[
          {
            required: hasTemplates,
            message: t("please_select_template"),
          },
        ]}
        onSelect={selectedTemplateHandler}
        templates={zoomTemplates}
      />

      <Form.Item className="mt-8">
        <Space>
          <Button
            htmlType="submit"
            type="primary"
            icon={<SendOutlined />}
            disabled={!hasTemplates}
          >
            {t("button.send")}
          </Button>
          <Button icon={<CloseOutlined />} onClick={onCancel}>
            {t("button.cancel")}
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

export default MeetingReminderForm;
